import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ADMIN_NAV, AdminNavSection, elementoActivo, esRutaAdmin } from './admin-nav.model';

/**
 * Navegación persistente del área de administración.
 *
 * Escritorio: columna lateral con las secciones y la actual resaltada.
 * Móvil: barra horizontal desplazable. En ambos casos, vuelta al resumen y al sitio público.
 */
@Component({
  selector: 'app-admin-nav',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './admin-nav.component.html',
  styleUrl: './admin-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminNavComponent {
  private router = inject(Router);

  readonly url = signal(this.router.url);
  readonly rol = signal(AdminNavComponent.leerRol());

  readonly activo = computed(() => elementoActivo(this.url()));
  readonly esAdmin = computed(() => this.rol() === 'ADMIN');
  readonly visible = computed(() => esRutaAdmin(this.url()) && ['ADMIN', 'EMPLEADO'].includes(this.rol()));

  readonly secciones = computed<AdminNavSection[]>(() => {
    const admin = this.esAdmin();
    return ADMIN_NAV.filter((s) => admin || !s.soloAdmin).map((s) => ({
      ...s,
      items: s.items.filter((i) => admin || !i.soloAdmin)
    }));
  });

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(inject(DestroyRef))
      )
      .subscribe((e) => {
        this.url.set((e as NavigationEnd).urlAfterRedirects);
        this.rol.set(AdminNavComponent.leerRol());
      });
  }

  seccionActual(seccion: AdminNavSection): boolean {
    const activo = this.activo();
    return !!activo && (seccion.route === activo || seccion.items.some((i) => i.route === activo));
  }

  private static leerRol(): string {
    try {
      return JSON.parse(localStorage.getItem('usuario') ?? '{}')?.rol ?? '';
    } catch {
      return '';
    }
  }
}
